import React, { useState } from "react";
import { omit } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Papa from "papaparse";
import Bluebird from "bluebird";
import firestore from "src/apiService/firestore";

import { Button, Typography } from "@material-ui/core";

import {
  addTransaction,
  markDownloadViewed,
  consolidateEbaySaleShippingAndAnalyticsCost,
} from "src/apiService";
import { setActiveDialog } from "src/store/adminHtml/actions";
import { setUploadsFromEbay, setPendingUploads } from "src/store/sale/actions";
import {
  getEbayExtraRows,
  getIsEbayImport,
  getPendingSalesUploads,
} from "src/store/sale/selector";
import { userIncomeTaxRateSelector } from "src/store/system/selector";
import { getNewSyncSalesDownloads } from "src/store/uploads/selector";
import { downloadCsv, returnsHeaders } from "src/utils";
import { getUserIdSync } from "src/config/storage";
import parallelize from "src/utils/parallelize";
import FirebaseBatcher from "src/utils/FirebaseBatcher";
import { finalizeSaleComputedFields } from "src/utils/sales";
import type { Sale } from "src/interfaces/sale.interface";

const db = firestore();

function CompleteUpload({ render }: any) {
  const dispatch = useDispatch();
  const uploads = useSelector(getPendingSalesUploads);
  const isEbayImport = useSelector(getIsEbayImport);
  const salesDownloads = useSelector(getNewSyncSalesDownloads);
  const { extraRows, returns } = useSelector(getEbayExtraRows);
  const incomeTaxRate = useSelector(userIncomeTaxRateSelector) ?? 0;

  const [loading, setLoading] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const handleClose = () => {
    setLoading(false);
    dispatch(setActiveDialog(""));
    setConfirmCancel(false);
  };

  const handleExpenseTransactions = (expenseRows) => {
    let promises: Promise<any>[] = [];
    let ebayExpensesOrderIdArray: Array<string> = [];
    const user = getUserIdSync();
    expenseRows.forEach((expense) => {
      let formattedExpense = {
        ...expense,
        reviewed: false,
        user,
      };
      promises.push(addTransaction(formattedExpense));
      if (formattedExpense.orderId) {
        ebayExpensesOrderIdArray.push(formattedExpense.orderId);
      }
    });

    return Bluebird.all(promises).tap(async () => {
      try {
        await consolidateEbaySaleShippingAndAnalyticsCost(
          ebayExpensesOrderIdArray
        );
      } catch (e) {
        console.log(
          "sales consolidation failed for: ",
          ebayExpensesOrderIdArray
        );
      }
    });
  };

  const cleanup = async () => {
    if (isEbayImport) {
      salesDownloads.forEach((download) => markDownloadViewed(download.id));
      if (extraRows && extraRows.length) {
        await handleExpenseTransactions(extraRows);
        dispatch(setActiveDialog("categorize_expenses"));
      }
      if (returns && returns.length) {
        const returnsCsv = Papa.unparse(returns, { columns: returnsHeaders });
        downloadCsv(
          new Blob([returnsCsv], { type: "text/csv" }),
          "eBayReturns"
        );
      }
    }
  };

  const submitUpload = async () => {
    const user = getUserIdSync();
    if (!uploads.length) {
      handleClose();
      return await cleanup();
    }
    handleClose();

    const prepareSaleForWrite = (sale: Sale, docId: string) =>
      finalizeSaleComputedFields(
        {
          ...sale,
          id: docId,
        },
        incomeTaxRate
      );

    const salesWithoutInventoryId: any = [];
    const salesByInventoryId: Record<string, any[]> = {};

    for (const sale of uploads) {
      const newItem: Sale = {
        // uuid is only for matching process, return_id is from inventory and should be omitted
        ...omit(sale, ["uuid", "return_id"]),
        user,
      };

      const inventoryId = newItem.inventoryId;

      if (inventoryId) {
        delete newItem.inventoryId;
        if (!salesByInventoryId[inventoryId])
          salesByInventoryId[inventoryId] = [];
        salesByInventoryId[inventoryId].push(newItem);
      } else {
        salesWithoutInventoryId.push(newItem);
      }
    }

    if (salesWithoutInventoryId.length) {
      const batch = new FirebaseBatcher();
      for (const sale of salesWithoutInventoryId) {
        const doc = db.collection("Sales").doc();
        await batch.set(doc, prepareSaleForWrite(sale, doc.id));
      }

      await batch.commit();
    }

    await parallelize(
      Object.entries(salesByInventoryId),
      async ([inventoryId, sales]) => {
        await db.runTransaction(async (t) => {
          let transactionInventory: any | undefined = undefined;
          let inventoryWasDeleted = false;

          const inventoryDoc = db.collection("Inventory").doc(inventoryId);
          const inventory = await t.get(inventoryDoc);
          if (inventory.exists) {
            const inventoryData = inventory.data();
            const quantity = inventoryData?.quantity || 1;
            const salesQuantity = sales.length;
            if (inventoryData?.transaction_inventory_id) {
              transactionInventory = (
                await t.get(
                  db
                    .collection("Transactions")
                    .doc(inventoryData.transaction_inventory_id)
                )
              ).data();
            }

            if (quantity > salesQuantity) {
              const salesPurchasePrice = sales.reduce(
                (price, sale) => price + (sale.purchase_price || 0),
                0
              );
              await t.update(inventoryDoc, {
                quantity: quantity - salesQuantity,
                purchase_price:
                  (inventoryData?.purchase_price || 0) - salesPurchasePrice,
              });
            } else {
              await t.delete(inventoryDoc);
              inventoryWasDeleted = true;
            }
          }

          const salesIds: string[] = [];
          for (const sale of sales) {
            const doc = db.collection("Sales").doc();
            const update = prepareSaleForWrite(sale, doc.id) as Record<
              string,
              unknown
            >;
            if (transactionInventory?.id)
              update.transaction_inventory_id = transactionInventory.id;
            await t.set(doc, update);
            salesIds.push(doc.id);
          }

          if (transactionInventory?.id) {
            let items = transactionInventory.items || [];
            if (inventoryWasDeleted)
              items = items.filter(
                (i) => i.type !== "inventory" || i.id !== inventoryId
              );
            for (const saleId of salesIds) {
              items.push({ type: "sale", id: saleId });
            }
            await t.update(
              db.collection("Transactions").doc(transactionInventory.id),
              { items }
            );
          }
        });
      },
      10
    );

    dispatch(setPendingUploads([]));
    await cleanup();
    if (isEbayImport) dispatch(setUploadsFromEbay(false));
    toast.success("Sales uploaded successfully.");
  };

  return render({
    title: "Complete Upload",
    main: (
      <Typography variant="body1" paragraph>
        {confirmCancel
          ? "Are you sure you want to cancel the upload? All of your inventory matching work will be lost."
          : `Your upload of ${uploads.length} sales is ready to submit.`}
      </Typography>
    ),
    actions: confirmCancel ? (
      <>
        <Button variant="contained" onClick={() => setConfirmCancel(false)}>
          No, Do Not Cancel
        </Button>
        <Button color="secondary" variant="contained" onClick={handleClose}>
          Yes, Cancel Upload
        </Button>
      </>
    ) : (
      <>
        <Button
          color="secondary"
          disabled={loading}
          variant="contained"
          onClick={() => setConfirmCancel(true)}
        >
          Cancel Upload
        </Button>
        <Button color="primary" variant="contained" onClick={submitUpload}>
          Submit
        </Button>
      </>
    ),
  });
}

export default CompleteUpload;
